import Swiper from "./swiper-bundle.esm.browser.min.js";

if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.configuration = null;
        this.handleKeyboard = this.handleKeyboard.bind(this);
        this.activeClass = "active";
      }

      connectedCallback() {
        this.readConfiguration();
        this.initializeSwiper();
        window.addEventListener("keydown", this.handleKeyboard);
        window.addEventListener("resize", () => this.swiper?.update());
      }

      disconnectedCallback() {
        if (this.swiper) this.swiper.destroy(true, true);
        window.removeEventListener("keydown", this.handleKeyboard);
      }

      handleKeyboard(event) {
        if (!this.swiper) return;
        const focusedElement = document.activeElement;
        if (!this.swiper.el.contains(focusedElement)) return;

        switch (event.key) {
          case "ArrowLeft":
            this.swiper.slidePrev();
            break;
          case "ArrowRight":
            this.swiper.slideNext();
            break;
        }
      }

      readConfiguration() {
        const defaultConfig = {
          slidesPerView: 1.35,
          spaceBetween: 16,
          freeMode: false,
          speed: 150,
          grabCursor: false,
          centeredSlides: true,
          loop: true,
          slideToClickedSlide: true,
          pagination: {
            el: ".swiper-pagination",
            clickable: true,
            renderBullet: (index, className) => {
              return `<span class="${className} swiper-pagination-bullet--svg-animation">
                        <svg width="20" height="20" viewBox="0 0 28 28">
                          <circle class="svg__circle" cx="14" cy="14" r="12" fill="none" stroke-width="2"></circle>
                          <circle class="svg__circle-inner" cx="14" cy="14" r="5" stroke-width="2"></circle>
                        </svg>
                      </span>`;
            },
          },
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          breakpoints: {
            600: { slidesPerView: 1.75 },
            900: { slidesPerView: 2.5 },
            1200: { slidesPerView: 4.5, spaceBetween: 24 },
          },
          on: {
            init: (swiper) => {
              this.handleTabindex(swiper);
              this.handleSlideChange(swiper);
              this.handleSoundToggle(swiper);
              this.observeSection();
            },
            slideChange: (swiper) => this.handleSlideChange(swiper),
            slideChangeTransitionEnd: (swiper) => this.handleTabindex(swiper),
          },
        };

        const configEl = this.querySelector('[data-swiper-configuration]');
        const customConfig = configEl ? JSON.parse(configEl.innerHTML) : {};
        this.configuration = { ...defaultConfig, ...customConfig };

        if (this.configuration.autoplay && window.innerWidth < 900) {
          this.configuration.autoplay = false;
        }
      }

      initializeSwiper() {
        const container = this.querySelector(".swiper");
        if (!container) return;
        this.swiper = new Swiper(container, this.configuration);
      }

      handleTabindex(swiper) {
        const focusableSelectors = "a, button, input, textarea, select, [tabindex]";
        swiper.slides.forEach((slide, index) => {
          const isActive = index === swiper.activeIndex;
          slide.querySelectorAll(focusableSelectors).forEach((el) => {
            el.setAttribute("tabindex", isActive ? "0" : "-1");
          });
        });
      }

      // --- VIDEO HELPERS ---
      pauseAllVideos() {
        this.querySelectorAll("video").forEach((video) => video.pause());
      }

      playVideoInActiveSlide(swiper) {
        const soundOn = swiper.el.dataset.sound === "on";
        const activeSlide = swiper.slides[swiper.activeIndex];
        const video = activeSlide?.querySelector("video");
        if (video) {
          video.play().catch(() => {});
          video.muted = !soundOn;
        }
      }

      updateAllVideosSound(swiper) {
        const soundOn = swiper.el.dataset.sound === "on";
        swiper.slides.forEach((slide) => {
          const video = slide.querySelector("video");
          if (video) video.muted = !soundOn;
        });
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".shoppable-video-reels__sound-btn");
          const video = slide.querySelector("video");
          if (!button || !video) return;

          button.addEventListener("click", () => {
            if (video.muted) {
              video.muted = false;
              swiper.el.dataset.sound = "on";
            } else {
              video.muted = true;
              swiper.el.dataset.sound = "off";
            }
            this.updateAllVideosSound(swiper);
          });
        });
      }

      handleSlideChange(swiper) {
        this.pauseAllVideos();
        swiper.slides.forEach((slide) => slide.classList.remove(this.activeClass));
        const activeSlide = swiper.slides[swiper.activeIndex];
        activeSlide?.classList.add(this.activeClass);
        this.playVideoInActiveSlide(swiper);
      }

      observeSection() {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const activeVideo = this.querySelector(".swiper-slide-active video");
              if (activeVideo) {
                if (entry.isIntersecting) {
                  activeVideo.play().catch(() => {});
                } else {
                  activeVideo.pause();
                }
              }
            });
          },
          { root: null, rootMargin: "0px", threshold: 0.1 }
        );
        observer.observe(this);
      }
    }
  );
}
